import { ToastService } from 'src/app/services/toast.service';
import { inject } from '@angular/core';
import { ChangeDetectorRef, Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { ActivatedRoute } from '@angular/router';
import { MapService } from 'src/app/services/map.service';
import { SubscriptionService } from '../subscription/services/subscription.service';
import { Subscription } from 'rxjs';
import { catchError, of } from 'rxjs';
import { MainDowngradeService } from '../subscription/subscription-downgrade/services/main-downgrade.service';
import { DowngradeStatusResponse } from '../subscription/subscription-downgrade/models/downgrade-response.model';
import { DowngradeCleanupComponent } from '../subscription/subscription-downgrade/modals/downgrade-cleanup/downgrade-cleanup.component';
import { TokenSuccessComponent } from '../shared/token-success-modal/token-success-modal.component';
import { ContentModuleService } from 'src/app/services/content-module.service';
import { ContentData } from '../content-creator/add-content/models/content.model';
import { featuredPin, SHARED_WORLDS } from './data/shared-worlds.data';
import {
  ComposerSignals,
  ContentKind,
  LandingBlock,
  SharedWorld,
  WorldPin,
} from './models/store-rail.model';
import { LandingRailComposerService } from './services/landing-rail-composer.service';
import { isPreviewMode, previewPlan, previewUser, seedPreviewAuth } from 'src/app/preview/preview.util';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
  private readonly toasts = inject(ToastService);
  readonly worlds: SharedWorld[] = SHARED_WORLDS;
  activeWorldKey: string = SHARED_WORLDS[0].key;
  selectedPinId: string = featuredPin(SHARED_WORLDS[0]).id;
  pageBlocks: LandingBlock[] = [];
  flippedId: string | null = null;
  planName = 'Adventurer';

  username: any;
  isLoading: boolean = false;
  userdata: any;
  hasPaidSubscription: boolean = false;
  statusData: DowngradeStatusResponse | null = null;
  recentContentModules: ContentData[] = [];
  highestRatedEstablishmentsModules: ContentData[] = [];
  isContentLoading: boolean = false;
  private subscriptionCheckSubscription: Subscription;
  private currentUserSubscription: Subscription;
  private downGradeRefreshSubscription: Subscription;
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;

  get activeWorld(): SharedWorld {
    return this.worlds.find((w) => w.key === this.activeWorldKey) ?? this.worlds[0];
  }

  get selectedPin(): WorldPin {
    return this.activeWorld.pins.find((p) => p.id === this.selectedPinId) ?? featuredPin(this.activeWorld);
  }

  /** Public maps in fixture order. Ashmorrow stays first as the marketing slot. */
  get visibleWorlds(): SharedWorld[] {
    const featured = this.worlds.find((w) => w.key === 'ashmorrow');
    const rest = this.worlds.filter((w) => w.key !== 'ashmorrow');
    return featured ? [featured, ...rest] : [...this.worlds];
  }

  constructor(
    private modalService: NgbModal,
    private router: Router,
    private currentUserService: CurrentUserService,
    private activatedRoute: ActivatedRoute,
    private mapService: MapService,
    private subscriptionService: SubscriptionService,
    private accountDowngradeService: MainDowngradeService,
    private contentModuleService: ContentModuleService,
    private cdr: ChangeDetectorRef,
    private railComposer: LandingRailComposerService,
  ) { }


  ngOnDestroy(): void {
    this.subscriptionCheckSubscription?.unsubscribe();
    this.currentUserSubscription?.unsubscribe();
    this.downGradeRefreshSubscription?.unsubscribe();
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    document.body.classList.remove('lp-hero-active');
  }

  logIn(): void {
    if (isPreviewMode()) {
      this.enterPreviewSession();
      return;
    }
    this.router.navigate(['/login']);
  }

  signUp(): void {
    if (isPreviewMode()) {
      this.enterPreviewSession();
      return;
    }
    this.router.navigate(['/signup']);
  }

  private enterPreviewSession(): void {
    seedPreviewAuth();
    this.currentUserService.setUserData(previewUser());
    this.currentUserService.setPlanUsageData(previewPlan());
    this.router.navigate(['/home']);
  }

  ngOnInit(): void {
    this.isLoading = true;
    document.body.classList.add('lp-hero-active');
    this.setupDownGradeRefresh();
    this.composeRails();

    const redirect = sessionStorage.getItem('redirect');
    this.activatedRoute.queryParams.subscribe((params) => {
      const tokenSuccess = params['token_purchased'];
      if (tokenSuccess) {
        this.openTokenSuccessModal(tokenSuccess);
      } else {
        this.runInItHome(redirect);
      }
      this.cdr.detectChanges();
    });

    this.fetchRecentContentModules();
  }

  openTokenSuccessModal(status: string) {
    const modalRef = this.modalService.open(TokenSuccessComponent, {
      size: 'md',
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });
    modalRef.componentInstance.status = status;
    modalRef.result.then((result) => {
      this.router.navigate([], {
        queryParams: { token_purchased: null },
        queryParamsHandling: 'merge',
      });
      this.cdr.detectChanges();
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.composeRails(), 150);
  }

  selectWorld(key: string): void {
    this.activeWorldKey = key;
    this.selectedPinId = featuredPin(this.activeWorld).id;
    this.flippedId = null;
    this.composeRails();
  }

  selectPin(pin: WorldPin, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedPinId = pin.id;
  }

  pinSpotClass(pin: WorldPin): string {
    const top = parseFloat(pin.top);
    const v = top < 32 ? 'top' : top > 68 ? 'bottom' : 'mid';
    return `lp-pin-spot--right lp-pin-spot--${v}`;
  }

  pinImage(pin: WorldPin): string {
    return pin.adventure.image || this.activeWorld.image;
  }

  onHeroTileActivate(pin: WorldPin, event: Event): void {
    event.stopPropagation();
    const id = 'pin-' + pin.id;
    if (this.isCoarsePointer()) {
      if (this.flippedId !== id) {
        event.preventDefault();
        this.flippedId = id;
        this.selectedPinId = pin.id;
        return;
      }
    }
    this.router.navigate(['/store'], { queryParams: { world: this.activeWorldKey } });
  }

  onHeroTileKey(pin: WorldPin, event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onHeroTileActivate(pin, event);
    }
  }

  onWorldCardClick(world: SharedWorld, event: Event): void {
    if (this.isCoarsePointer()) {
      const id = 'world-' + world.key;
      if (this.flippedId !== id) {
        event.preventDefault();
        this.flippedId = id;
        return;
      }
    }
    this.selectWorld(world.key);
  }

  isFlipped(id: string): boolean {
    return this.flippedId === id;
  }

  trackRail(_i: number, block: LandingBlock): string {
    return block.kind === 'rail' ? block.rail.id : 'featured-map';
  }

  private isCoarsePointer(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;
  }

  private composeRails(): void {
    const signals: ComposerSignals = {
      userId: this.userdata?.id ?? null,
      planName: this.planName,
      purchasedTypes: this.stubPurchasedTypes(),
      searchFacets: this.stubSearchFacets(),
      activeWorldKey: this.activeWorldKey,
      activeWorldName: this.activeWorld.name,
      accessibleMaps: this.worlds.map((world) => ({
        key: world.key,
        name: world.name,
        genre: world.genre,
      })),
    };
    const width = typeof window !== 'undefined' ? window.innerWidth : 1400;
    this.pageBlocks = this.railComposer.compose(signals, width);
  }

  /** Stub until store purchase history exists. */
  private stubPurchasedTypes(): ContentKind[] {
    if (!this.userdata?.id) return [];
    return [];
  }

  /** Stub until store search history exists. */
  private stubSearchFacets(): ComposerSignals['searchFacets'] {
    return [];
  }

  private setupDownGradeRefresh() {
    this.downGradeRefreshSubscription = this.currentUserService.refreshDowngrade.subscribe((res: boolean) => {
      if (res === true && this.userdata?.id) {
        this.fetchDowngradeStatus(this.userdata.id);
      }
      this.cdr.detectChanges();
    });
  }

  private runInItHome(redirect: string) {
    if (redirect) {
      this.router.navigate(['invitationpage']);
      sessionStorage.removeItem('redirect');
    }
    this.currentUserSubscription = this.currentUserService.currentUser.subscribe((data: any | null) => {
      if (data) {
        this.isLoading = false;
        this.userdata = data;
        this.mapService.getMapList(this.userdata.id);
        this.fetchDowngradeStatus(this.userdata.id);
        this.checkSubscriptionStatus();
        this.composeRails();
        this.cdr.detectChanges();
      } else {
        this.isLoading = false;
        this.hasPaidSubscription = false;
        this.planName = 'Adventurer';
        this.composeRails();
        this.cdr.detectChanges();
      }
    });
  }

  fetchDowngradeStatus(userId: number): void {
    this.isLoading = true;
    this.accountDowngradeService.getDowngradeStatus(userId).subscribe({
      next: (response: DowngradeStatusResponse) => {
        this.statusData = response;
        this.isLoading = false;

        if (response.should_show_warning || response.cleanup_required) {
          this.startDowngradeFlow(response);
        }
      
          this.cdr.detectChanges();},
      error: (error) => {
        console.error('Error fetching downgrade status:', error);
        this.isLoading = false;
      
          this.cdr.detectChanges();}
    });
  }

  showDowngradeWarningPopup(): void {
  }

  startDowngradeFlow(response: DowngradeStatusResponse) {
    const modalRef = this.modalService.open(DowngradeCleanupComponent, {
      centered: true,
      size: 'md',
      backdrop: 'static',
    });
    modalRef.componentInstance.userId = this.userdata?.id;
    modalRef.componentInstance.downgradeResponse = response;
    modalRef.componentInstance.upgradePlan = response.to_plan;
    modalRef.componentInstance.currentPlan = response.from_plan;

    modalRef.result.then((result) => {
      if (result === 'next') {
      } else if (result === 'close') {
      }
    
          this.cdr.detectChanges();});
  }

  private checkSubscriptionStatus(): void {
    if (!this.userdata?.id) {
      this.hasPaidSubscription = false;
      return;
    }

    this.subscriptionCheckSubscription = this.subscriptionService
      .getCurrentSubscription(this.userdata.id)
      .pipe(
        catchError(() => {
          this.hasPaidSubscription = false;
          return of(null);
        })
      )
      .subscribe((planRes: any) => {
        if (planRes && planRes.plan) {
          const planName = (planRes.plan || '').toString().toLowerCase();
          this.planName = planRes.plan;
          this.hasPaidSubscription = planName && planName !== 'adventurer';
        } else {
          this.hasPaidSubscription = false;
          this.planName = 'Adventurer';
        }
        this.composeRails();
        this.cdr.detectChanges();
      });
  }

  navigateToSubscription(): void {
    this.router.navigate(['/subscription'], {
      queryParams: {
        isUser: this.userdata ? true : false,
        showPlans: true
      }
    });
  }

  private fetchRecentContentModules() {
    this.isContentLoading = true;
    this.contentModuleService
      .getRecentContentModules()
      .pipe(
        catchError(() => {
          return of(null);
        })
      )
      .subscribe((res: { latest_content: ContentData[], highest_rated_establishments: { header: string, results: ContentData[] } }) => {
        if (res && res.latest_content) {
          this.recentContentModules = res.latest_content;
        } else {
          this.recentContentModules = [];
        }

        if (res && res.highest_rated_establishments?.results) {
          this.highestRatedEstablishmentsModules = res.highest_rated_establishments.results;
        } else {
          this.highestRatedEstablishmentsModules = [];
        }
        this.isContentLoading = false;
        this.cdr.detectChanges();
      });
  }

  handleNavigationToMap() {
    return;
  }

  navigateToWP(module: ContentData) {
    if (module.product_url && module.product_url != '') {
      window.open(module.product_url, '_blank');
    }
    else {
      this.toasts.showNote("This item isn't available in the store.", 'warning')
    }
  }
}
